import React from 'react';

import { spacing, useClassnames } from '@lumapps/classnames';
import { ReCaptcha } from '@lumapps/lumx-inputs/components/Captcha';
import { StepProps } from '@lumapps/lumx-layouts/types';
import { Button, Kind, Message, Text, TextField } from '@lumapps/lumx/react';
import { useQueryParams } from '@lumapps/router';
import { SIGN_IN } from '@lumapps/sign-in/keys';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { isValidEmail } from '@lumapps/utils/string/isValidEmail';

import { errorMessage } from '../../utils/displayErrorMessage';

import './index.scss';

const CLASSNAME = 'send-email-form';
export interface SendEmailFormProps extends StepProps {
    /** callback to set the current wizard state */
    setWizardState: (state: { email: string }) => void;
    /** Whether is magic link login or not */
    isMagicLink?: boolean;
    /** the confirm button text  */
    buttonText: string;
    /** the form id */
    formId: string;
    /** the title id   */
    titleId: string;
    /** optional submit handler — when provided, intercepts the native form POST */
    onSubmit?: (email: string) => void;
    /** whether the form submission resulted in an API error */
    hasSubmitError?: boolean;
    /** whether is submitting */
    isSubmitting?: boolean;
}
export const SendEmailForm: React.FC<SendEmailFormProps> = ({
    setWizardState,
    buttonText,
    isMagicLink = false,
    titleId,
    formId,
    onSubmit: onSubmitProp,
    hasSubmitError = false,
    isSubmitting,
}) => {
    const { translateKey, translateAndReplace } = useTranslate();
    const [email, setEmail] = React.useState<string>('');
    const [captcha, setCaptcha] = React.useState<string>('');
    const [hasError, setHasError] = React.useState<boolean>(false);
    const [errorEmail, setErrorEmail] = React.useState<string>('');
    const { block, element } = useClassnames(CLASSNAME);
    const { r: redirectUri, nextUrl, error, correlationId } = useQueryParams();

    /**
     * Trigger when the captcha change.
     *
     * @param {string} value The response of the captcha.
     */
    function onChange(value: string) {
        setCaptcha(value);
    }

    /**
     * Return error, if email haven't a valid format
     *
     */
    const controlEmailAddress = (value: string) => {
        if (!isValidEmail(value)) {
            setErrorEmail(translateKey(SIGN_IN.INVALID_EMAIL_ERROR));
            setHasError(true);
        } else {
            setErrorEmail('');
            setHasError(false);
        }
    };

    function onChangeEmail(value: string) {
        setEmail(value);
        controlEmailAddress(value);
        setWizardState({ email: value });
    }

    const onSubmit = React.useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            if (!onSubmitProp) {
                return;
            }
            event.preventDefault();
            onSubmitProp(email);
        },
        [email, onSubmitProp],
    );
    /**
     * Since we are bound to the current login endpoint
     * and it doesn't accept the ReCaptchaRef attribute in the payload
     * we need to move it out of the form. And since
     * the TextField needs to be above the ReCaptcha, we also set it out of the form.
     * TODO: Move both fields back inside the form once the backend manages ReCaptcha value.
     */
    return (
        <>
            <div className={block()}>
                <TextField
                    form={formId}
                    onChange={onChangeEmail}
                    className={element('input')}
                    label={translateKey(GLOBAL.EMAIL)}
                    error={errorEmail}
                    hasError={hasError}
                    value={email}
                    autoComplete="email"
                />

                {!isMagicLink && <ReCaptcha form={formId} className={element('re-captcha')} handleChange={onChange} />}
            </div>

            <form
                id={formId}
                aria-labelledby={titleId}
                method="POST"
                className={block()}
                action={window.location.pathname}
                onSubmit={onSubmit}
            >
                <input id="email" type="hidden" name="email" value={email} />

                {!isMagicLink && <input id="captcha" type="hidden" name="captcha" value={captcha} />}
                {(redirectUri || nextUrl) && (
                    <input id="nextUrl" type="hidden" name="nextUrl" value={redirectUri || nextUrl} />
                )}

                <Button
                    disabled={(!isMagicLink && captcha === '') || !isValidEmail(email) || isSubmitting}
                    className={element('button')}
                    type="submit"
                    fullWidth
                >
                    {buttonText}
                </Button>

                {errorMessage(error) && (
                    <Message className={spacing('margin', 'top', 'big')} kind={Kind.error} hasBackground>
                        <Text as="p"> {translateKey(errorMessage(error))} </Text>

                        {correlationId && (
                            <Text as="p">
                                {translateAndReplace(SIGN_IN.GENERIC_ERROR, {
                                    CORRELATION_ID: correlationId,
                                })}
                            </Text>
                        )}
                    </Message>
                )}

                {hasSubmitError && (
                    <Message className={spacing('margin', 'top', 'big')} kind={Kind.error} hasBackground>
                        <Text as="p">{translateKey(GLOBAL.GENERIC_ERROR)}</Text>
                    </Message>
                )}
            </form>
        </>
    );
};
